/**
 * ClaudeTuiAdapter — the extracted Claude-specific half of the former
 * hard-wired PtySession behavior. Pure delegation to the existing modules:
 * zero behavior change is the contract (pinned by the keep-alive and int
 * tests). resolveBinary stays deliberately lenient (never throws): claude is
 * always baked into the pod image and the exit diagnostics already explain a
 * missing binary; new adapters should fail loudly instead.
 */
import { buildExitErrors, buildSpawnArgs, spawnOptionsFingerprint } from "../spawn-args.js";
import { ensureClaudeCredentials, ensureClaudeOnboarding } from "../credentials.js";
import { buildPromptBytes, inheritedEnv } from "../pty-support.js";
import type {
  TuiAdapter,
  TuiCapabilities,
  TuiFingerprintInput,
  TuiSpawnInput,
  TuiSpawnSpec,
} from "./types.js";

export class ClaudeTuiAdapter implements TuiAdapter {
  readonly id = "claude-code" as const;
  readonly capabilities: TuiCapabilities = {
    resume: true,
    structuredEvents: true,
    prefill: true,
    passiveTurns: true,
  };

  resolveBinary(env: NodeJS.ProcessEnv = process.env): string {
    return env.CONVEYOR_CLAUDE_BIN ?? "claude";
  }

  buildSpawn(input: TuiSpawnInput): TuiSpawnSpec {
    const { options, resume } = input;
    const args = buildSpawnArgs({
      ...(resume ? { resume } : {}),
      ...(options.sessionId ? { sessionId: options.sessionId } : {}),
      model: options.model,
      permissionMode: options.permissionMode,
      settingsPath: input.settingsPath ?? "",
      ...(options.appendSystemPrompt ? { appendSystemPrompt: options.appendSystemPrompt } : {}),
      ...(input.mcpConfigPath ? { mcpConfigPath: input.mcpConfigPath, strictMcpConfig: true } : {}),
    });
    return {
      file: this.resolveBinary(),
      args,
      env: inheritedEnv(input.hookSocketPath),
    };
  }

  async prepareEnvironment(opts: { cwd: string; env?: NodeJS.ProcessEnv }): Promise<void> {
    const env = opts.env ?? process.env;
    await ensureClaudeCredentials(env);
    await ensureClaudeOnboarding(env, opts.cwd);
  }

  spawnFingerprint(input: TuiFingerprintInput): string {
    return spawnOptionsFingerprint(input);
  }

  encodePromptBytes(text: string): string {
    return buildPromptBytes(text);
  }

  buildExitErrors(exitCode: number, rawOutput: string): string[] {
    return buildExitErrors(exitCode, rawOutput, this.resolveBinary());
  }
}
